import { memo, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import { PIE_ALLOWED_COLORS } from '../../constants/gerencialesConstants';

/**
 * Renderiza los graficos de suscriptores en app mobile.
 * @param {Object} props - Props del componente.
 * @param {Object} props.chartData - Datos de gráficos desde el backend.
 * @returns {JSX.Element} Secciones de gráficos.
 */
const AppSubscribersCharts = ({ chartData }) => {
  const usageRows = Array.isArray(chartData?.app_usage) ? chartData.app_usage : [];
  const menByAgeRows = Array.isArray(chartData?.men_by_age) ? chartData.men_by_age : [];
  const womenByAgeRows = Array.isArray(chartData?.women_by_age) ? chartData.women_by_age : [];
  const appByAgeRows = Array.isArray(chartData?.app_by_age) ? chartData.app_by_age : [];

  const formatCount = (value) => {
    const numeric = Number(value ?? 0);
    if (Number.isNaN(numeric)) return '0';
    return numeric.toLocaleString('es-AR');
  };

  const usagePieData = useMemo(
    () =>
      usageRows.map((row, index) => ({
        name: row.label,
        value: Number(row.count ?? 0),
        fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
      })),
    [usageRows]
  );

  const buildAgeBarData = (rows) =>
    rows.map((row) => ({
      range: row.range,
      with_count: Number(row.with_count ?? 0),
      without_count: Number(row.without_count ?? 0),
    }));

  const menByAgeData = useMemo(() => buildAgeBarData(menByAgeRows), [menByAgeRows]);
  const womenByAgeData = useMemo(() => buildAgeBarData(womenByAgeRows), [womenByAgeRows]);
  const appByAgeData = useMemo(() => buildAgeBarData(appByAgeRows), [appByAgeRows]);

  const ChartCard = ({ title, chartId, children }) => (
    <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
      <Box
        sx={{
          backgroundColor: '#3b3f46',
          color: 'white',
          px: 2.5,
          py: 1.5,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          fontSize: 30,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>
      <Box id={chartId} sx={{ p: 2 }}>
        {children}
      </Box>
    </Paper>
  );

  return (
    <>
      <ChartCard title="Uso de la app vs No uso" chartId="gerenciales-app-subscribers-usage">
        <Box sx={{ height: { xs: 280, md: 320 } }}>
          {usagePieData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={usagePieData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={110}
                  isAnimationActive={false}
                  label={({ name, value }) => `${name}: ${formatCount(value)}`}
                >
                  {usagePieData.map((entry, index) => (
                    <Cell key={`cell-${entry.name}-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard
        title="Uso de la app vs No uso en hombres por rango etario"
        chartId="gerenciales-app-subscribers-men-age"
      >
        <Box sx={{ height: { xs: 280, md: 340 } }}>
          {menByAgeData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={menByAgeData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis tickFormatter={(value) => formatCount(value)} />
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
                <Bar
                  dataKey="with_count"
                  name="Usa app"
                  fill={PIE_ALLOWED_COLORS[0]}
                  isAnimationActive={false}
                  stackId="usage"
                />
                <Bar
                  dataKey="without_count"
                  name="No usa app"
                  fill={PIE_ALLOWED_COLORS[4]}
                  isAnimationActive={false}
                  stackId="usage"
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard
        title="Uso de la app vs No uso en mujeres por rango etario"
        chartId="gerenciales-app-subscribers-women-age"
      >
        <Box sx={{ height: { xs: 280, md: 340 } }}>
          {womenByAgeData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={womenByAgeData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis tickFormatter={(value) => formatCount(value)} />
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
                <Bar
                  dataKey="with_count"
                  name="Usa app"
                  fill={PIE_ALLOWED_COLORS[1]}
                  isAnimationActive={false}
                  stackId="usage"
                />
                <Bar
                  dataKey="without_count"
                  name="No usa app"
                  fill={PIE_ALLOWED_COLORS[5]}
                  isAnimationActive={false}
                  stackId="usage"
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard
        title="Uso de la app vs No uso en general por rango etario"
        chartId="gerenciales-app-subscribers-general-age"
      >
        <Box sx={{ height: { xs: 280, md: 340 } }}>
          {appByAgeData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={appByAgeData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis tickFormatter={(value) => formatCount(value)} />
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
                <Bar
                  dataKey="with_count"
                  name="Usa app"
                  fill={PIE_ALLOWED_COLORS[2]}
                  isAnimationActive={false}
                  stackId="usage"
                />
                <Bar
                  dataKey="without_count"
                  name="No usa app"
                  fill={PIE_ALLOWED_COLORS[6]}
                  isAnimationActive={false}
                  stackId="usage"
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>
    </>
  );
};

export default memo(AppSubscribersCharts);
